import { useTranslation } from 'react-i18next';
import { Badge, Avatar, type AvatarColor } from '../../components/core';
import type { AppRole } from '../../services/appUser.service';
import type { OrgMember } from '../../services/org.service';
import { adjustBrightness } from '../../utils/color';

function getInitials(name: string): string {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function getMemberGradient(member: OrgMember): string | undefined {
  if (!member.customAvatarColor) return undefined;
  return `linear-gradient(135deg, ${adjustBrightness(member.customAvatarColor, 1.3)} 0%, ${member.customAvatarColor} 100%)`;
}

interface MemberRowProps {
  member: OrgMember;
  isSelf: boolean;
  isToggling: boolean;
  onToggleDisabled: (_uid: string, _isDisabled: boolean) => Promise<void>;
  isLast?: boolean;
  isLastActiveAdmin?: boolean;
  roles: AppRole[];
  onChangeRole: (_uid: string, _roleId: string) => Promise<void>;
}

export function MemberRow({ member, isSelf, isToggling, onToggleDisabled, isLast, isLastActiveAdmin, roles, onChangeRole }: MemberRowProps) {
  const { t } = useTranslation();
  const isDisabledOrLastAdmin = isSelf || (isLastActiveAdmin && !member.isDisabled);
  let buttonTitle = t('members.disableMember');
  if (isSelf) {
    buttonTitle = t('members.cantDisableYourself');
  } else if (member.isDisabled) {
    buttonTitle = t('members.reEnableMember');
  } else if (isLastActiveAdmin) {
    buttonTitle = t('members.cantDisableLastAdmin');
  }

  return (
    <tr className={!isLast ? 'border-b border-green-100' : ''}>
      <td className="whitespace-nowrap px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar
            size="sm"
            variant="user"
            avatarColor={(member.avatarColor as AvatarColor) || 'mint'}
            customGradient={member.isDisabled ? undefined : getMemberGradient(member)}
          >
            {getInitials(member.displayName || member.email)}
          </Avatar>
          <div className="font-body text-sm font-medium text-gray-900">
            {member.displayName}
            {isSelf && <span className="ms-1 text-xs italic text-gray-400">{t('members.youLabel')}</span>}
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-3 font-body text-sm text-gray-600">
        {member.email}
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        {!isSelf && roles.length > 0 ? (
          <select
            value={member.role}
            onChange={(e) => onChangeRole(member.uid, e.target.value)}
            disabled={isToggling}
            className={`rounded-full px-2 py-0.5 text-xs font-medium font-body border-0 cursor-pointer ${
              member.role === 'admin'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-700'
            } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {roles.map(role => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </select>
        ) : (
          <Badge variant={member.role === 'admin' ? 'primary' : 'secondary'}>
            {member.role === 'admin' ? t('members.roleAdmin') : t('members.roleOperator')}
          </Badge>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <div className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-body text-xs font-medium ${
          member.isDisabled
            ? 'bg-gray-100 text-gray-600'
            : 'bg-green-100 text-green-700'
        }`}>
          <span>{member.isDisabled ? '○' : '●'}</span>
          {member.isDisabled ? t('members.statusDisabled') : t('members.statusActive')}
        </div>
      </td>
      <td className="px-4 py-3 text-end">
        <button
          type="button"
          onClick={() => onToggleDisabled(member.uid, !member.isDisabled)}
          disabled={isDisabledOrLastAdmin || isToggling}
          className={`inline-flex h-8 w-8 items-center justify-center rounded text-lg ${
            isDisabledOrLastAdmin
              ? 'cursor-not-allowed opacity-30'
              : member.isDisabled
                ? 'text-green-700 hover:bg-green-50'
                : 'text-red-600 hover:bg-red-50'
          }`}
          title={buttonTitle}
        >
          {member.isDisabled ? '✓' : '🚫'}
        </button>
      </td>
    </tr>
  );
}

interface MemberCardProps {
  member: OrgMember;
  isSelf: boolean;
  isToggling: boolean;
  onToggleDisabled: (_uid: string, _isDisabled: boolean) => Promise<void>;
  isLastActiveAdmin?: boolean;
  roles: AppRole[];
  onChangeRole: (_uid: string, _roleId: string) => Promise<void>;
}

export function MemberCard({ member, isSelf, isToggling, onToggleDisabled, isLastActiveAdmin, roles, onChangeRole }: MemberCardProps) {
  const { t } = useTranslation();
  const isDisabledOrLastAdmin = isSelf || (isLastActiveAdmin && !member.isDisabled);
  let buttonTitle = t('members.disableMember');
  if (isSelf) {
    buttonTitle = t('members.cantDisableYourself');
  } else if (member.isDisabled) {
    buttonTitle = t('members.reEnableMember');
  } else if (isLastActiveAdmin) {
    buttonTitle = t('members.cantDisableLastAdmin');
  }

  return (
    <div className={`p-3 ${member.isDisabled ? 'opacity-60' : ''}`}>
      <div className="flex gap-3">
        <Avatar
          size="sm"
          variant="user"
          avatarColor={(member.avatarColor as AvatarColor) || 'mint'}
          customGradient={member.isDisabled ? undefined : getMemberGradient(member)}
        >
          {getInitials(member.displayName || member.email)}
        </Avatar>
        <div className="flex flex-1 flex-col gap-2">
          <div className="font-body text-sm font-medium text-gray-900">
            {member.displayName}
            {isSelf && <span className="ms-1 text-xs text-gray-400">{t('members.youLabel')}</span>}
          </div>
          <div className="font-body text-xs text-gray-500">{member.email}</div>
          <div className="flex gap-2">
            {!isSelf && roles.length > 0 ? (
              <select
                value={member.role}
                onChange={(e) => onChangeRole(member.uid, e.target.value)}
                disabled={isToggling}
                className={`rounded-full px-2 py-0.5 text-xs font-medium font-body border-0 cursor-pointer ${
                  member.role === 'admin'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-700'
                } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            ) : (
              <Badge variant={member.role === 'admin' ? 'primary' : 'secondary'}>
                {member.role === 'admin' ? t('members.roleAdmin') : t('members.roleOperator')}
              </Badge>
            )}
            <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-body text-xs font-medium ${
              member.isDisabled
                ? 'bg-gray-100 text-gray-600'
                : 'bg-green-100 text-green-700'
            }`}>
              <span>{member.isDisabled ? '○' : '●'}</span>
              {member.isDisabled ? t('members.statusDisabled') : t('members.statusActive')}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onToggleDisabled(member.uid, !member.isDisabled)}
          disabled={isDisabledOrLastAdmin || isToggling}
          className={`flex h-8 w-8 items-center justify-center rounded text-lg ${
            isDisabledOrLastAdmin
              ? 'cursor-not-allowed opacity-30'
              : member.isDisabled
                ? 'text-green-700 hover:bg-green-50'
                : 'text-red-600 hover:bg-red-50'
          }`}
          title={buttonTitle}
        >
          {member.isDisabled ? '✓' : '🚫'}
        </button>
      </div>
    </div>
  );
}
